/**
 * Der Zugang zum Freundinnen-Raum.
 *
 * Die Zugehörigkeit steckt in einem Token im localStorage. Kein Passwort, kein
 * Login-Bildschirm: Ein Achtjähriges soll die App öffnen und loslegen können.
 * Das Geheimnis ist der Raum-Code, den ein Erwachsener weitergibt.
 */

export interface RaumMitglied {
  spitzname: string
  heuteGeschafft: boolean
  ichSelbst: boolean
}

export interface Tagesstand {
  raum: { code: string; name: string }
  datum: string
  ichHabeGeschafft: boolean
  mitglieder: RaumMitglied[]
  raetsel: {
    frage: string
    /** Teilweise maskiert, solange nicht aufgelöst ist. Punkte stehen für verdeckte Zeichen. */
    pointe: string
    vollAufgedeckt: boolean
    grund: 'alle-da' | 'uhrzeit' | null
    aufloesungStunde: number
    geschafftHeute: number
    mitgliederAnzahl: number
  }
  reise: { rundenGesamt: number }
  neuGeschafft?: boolean
}

export interface Zugang {
  token: string
  code: string
  spitzname: string
  raumName: string
}

const SCHLUESSEL = 'zahlenflug:raum-zugang'

export function zugangLesen(): Zugang | null {
  try {
    const roh = window.localStorage.getItem(SCHLUESSEL)
    return roh ? (JSON.parse(roh) as Zugang) : null
  } catch {
    return null
  }
}

export function zugangSpeichern(zugang: Zugang): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, JSON.stringify(zugang))
  } catch {
    /* Ohne Speicher muss beim nächsten Start erneut beigetreten werden. */
  }
}

export function zugangLoeschen(): void {
  try {
    window.localStorage.removeItem(SCHLUESSEL)
  } catch {
    /* nichts zu tun */
  }
}

async function anfragen<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  const zugang = zugangLesen()
  const antwort = await fetch(pfad, {
    ...optionen,
    headers: {
      'content-type': 'application/json',
      ...(zugang ? { 'x-zahlenflug-token': zugang.token } : {}),
      ...optionen.headers,
    },
  })

  const inhalt = await antwort.json().catch(() => ({}))
  if (!antwort.ok) {
    throw new RaumFehler((inhalt as { fehler?: string }).fehler ?? 'unbekannt', antwort.status)
  }
  return inhalt as T
}

export class RaumFehler extends Error {
  readonly grund: string
  readonly status: number

  constructor(grund: string, status: number) {
    super(grund)
    this.grund = grund
    this.status = status
  }
}

export async function raumAnlegen(name: string): Promise<{ code: string }> {
  return anfragen('/api/raum', { method: 'POST', body: JSON.stringify({ name }) })
}

export async function beitreten(code: string, spitzname: string): Promise<Zugang> {
  const antwort = await anfragen<{
    token: string
    spitzname: string
    raum: { code: string; name: string }
  }>(`/api/raum/${encodeURIComponent(code)}/beitreten`, {
    method: 'POST',
    body: JSON.stringify({ spitzname }),
  })

  const zugang: Zugang = {
    token: antwort.token,
    code: antwort.raum.code,
    spitzname: antwort.spitzname,
    raumName: antwort.raum.name,
  }
  zugangSpeichern(zugang)
  return zugang
}

export async function tagesstandHolen(): Promise<Tagesstand | null> {
  const zugang = zugangLesen()
  if (!zugang) return null
  return anfragen<Tagesstand>(`/api/raum/${encodeURIComponent(zugang.code)}/heute`)
}

/**
 * Meldet die heutige Runde an den Raum.
 *
 * Wird nach jedem Rundenende aufgerufen und darf niemals etwas kaputtmachen:
 * Ist kein Raum eingerichtet oder der Server nicht erreichbar, übt Lina
 * einfach weiter, als gäbe es keinen Raum. Der Trainer funktioniert ohne
 * Netz, und das soll er auch behalten.
 */
export async function rundeMelden(
  aufgaben: number,
  geschafft: boolean,
): Promise<Tagesstand | null> {
  const zugang = zugangLesen()
  if (!zugang) return null
  try {
    return await anfragen<Tagesstand>(`/api/raum/${encodeURIComponent(zugang.code)}/geschafft`, {
      method: 'POST',
      body: JSON.stringify({ aufgaben, geschafft }),
    })
  } catch {
    return null
  }
}
